import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import {
  type ApiResponse,
  addActionNameToMutationResult,
  handleZodError,
  useLegacyAxiosInstance,
} from './utils';

const ClientFormTncSchema = z.object({
  termsAndConditionsId: z.number(),
});

const ClientFormTncSnakeCase = ClientFormTncSchema.transform(data => ({
  terms_and_conditions_id: data.termsAndConditionsId,
}));

export type UpdateClientTnc = z.infer<typeof ClientFormTncSchema>;

export async function updateClientTnc(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: { id: string; data: UpdateClientTnc },
) {
  const instance = await axiosInstance;
  const safeParse = ClientFormTncSnakeCase.safeParse(data);
  if (!safeParse.success) {
    handleZodError(safeParse, 'client T&Cs');
  }

  return instance.put(`/debtors/${id}`, ClientFormTncSnakeCase.parse(data));
}

export function useUpdateClientTnc() {
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientTnc }
  >({
    mutationFn: variables => updateClientTnc(axiosInstance, variables),
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientTnc },
    'updateClientTnc'
  >('updateClientTnc', mutation);
}
